/**
 * Gradwear - personalization charge reconciliation (cart page only).
 *
 * WHY THIS EXISTS
 * ---------------
 * Personalization "addons" are Bold Product Options 2.0 running in hybrid /
 * v1_variant_mode. The theme never adds the charge itself: assets/product.js
 * (ProductCustomization.updateCharges) stamps hidden properties onto the PARENT
 * product form...
 *
 *   _boldVariantIds    CSV of the charge VARIANT ids
 *   _boldVariantQtys   CSV of how many personalization options map to each of
 *                      those variant ids  <-- the source of truth for this file
 *   _boldVariantPrices one price per selected option
 *   _boldBuilderId     the parent/child link, value is `new Date()` stringified
 *
 * ...and Bold's remotely hosted options.js then creates the separate charge line
 * items, stamping each with the parent's _boldBuilderId and NO _boldVariantIds.
 * snippets/bold-options-hybrid-cart-item.liquid uses exactly that asymmetry to
 * hide the charge rows, and sections/cart.liquid re-prints the charge as text
 * under the parent from the parent's own _boldVariantPrices.
 *
 * Because the charge line items are created by third-party remote JS, that step
 * is unreliable - carts routinely end up with more charge line items than there
 * were personalization options selected ("one product but multiple addons"),
 * occasionally with fewer, and with orphans left behind after a parent is
 * removed. The rows are invisible on the cart page, so the only symptom the
 * shopper sees is a wrong subtotal.
 *
 * WHAT THIS DOES
 * --------------
 * On each cart page load it compares the charge line items actually in the cart
 * against the target recorded on the parent line items, and corrects the cart:
 *
 *   actual > target  ->  reduce to target (zero duplicate rows, trim survivor)
 *   actual < target  ->  /cart/add.js the shortfall with the parent's builder id
 *   no parent at all ->  remove the orphan charge
 *   actual == target ->  nothing, no network write, no reload
 *
 * The target is (the parent's _boldVariantQtys for that charge variant) x (the
 * parent's line quantity) - every unit of a personalized product needs its own
 * charge. product.js writes _boldVariantQtys from the checked option boxes
 * alone, so it never accounts for quantity; see MULTIPLY_BY_PARENT_QUANTITY.
 *
 * THE LEGACY BLANKET PURGE
 * ------------------------
 * assets/bms.cart.bundle.js (built from
 * src/modules/cart/presenters/PersonalizationChargePresenter.js) deletes EVERY
 * line item whose variant is 44941624067 on cart load, then hard-reloads.
 *
 * Confirmed against a live cart on 2026-09-02: a personalized parent line item
 * carried `_boldVariantIds = "44941624067"`. The theme's own product.js writes
 * that value from the charge product's first variant id, so 44941624067 IS the
 * live personalization charge variant - and the legacy purge has been deleting
 * the real charge on every single cart load. That is the bug, and it predates
 * this file. (Products whose personalization metafield names a school-specific
 * charge product resolve to a different variant id and survive the purge, which
 * is why the symptom looked intermittent.)
 *
 * So, with TAKE_OVER_LEGACY_PURGE on, this file neutralises the legacy
 * presenter AT RUNTIME - it replaces BOLD.BMS.BUILDS.Cart.PersonalizationCharge
 * .init with a no-op before DOMContentLoaded fires, which is why this script
 * must load after bms.cart-init.liquid - and then reconciles 44941624067 like
 * any other charge variant. No existing theme file is edited.
 *
 * DEBUGGING  ->  add ?pcfix=debug to the cart URL
 * ----------------------------------------------
 *   /cart?pcfix=debug     full text report in an on-page panel + console,
 *                         with a Copy button. Keeps fixing the cart normally.
 *                         Persists across reloads, and the report accumulates
 *                         one section per pass - so reload loops are visible.
 *   /cart?pcfix=dry       same report, but never writes to the cart.
 *   /cart?pcfix=legacy    let the legacy blanket purge run as it used to and
 *                         stand down, for before/after comparison.
 *                         Combine: /cart?pcfix=dry,legacy
 *   /cart?pcfix=off       turn debug back off.
 *
 * From the console at any time:
 *   GradwearPersonalizationChargeFix.run()      re-run a pass
 *   GradwearPersonalizationChargeFix.report()   re-print the last report text
 *   GradwearPersonalizationChargeFix.settled()  promise for the scheduled passes
 *   GradwearPersonalizationChargeFix.reset()    clear debug mode + loop guard
 *
 * TIMING / WHY THERE IS NO SECOND REFRESH
 * ---------------------------------------
 * A cart fix used to cost the shopper two navigations: their own "Update cart"
 * post, then a reload from this script a few seconds later. Three things
 * removed that second one:
 *   - /cart.js is requested at script-execution time, in parallel with page
 *     parsing, instead of after everything has loaded;
 *   - reconciliation runs at DOMContentLoaded instead of window load, which no
 *     longer waits for every product image on the page;
 *   - once the cart is corrected the page is patched in place - see
 *     SEAMLESS_UPDATE - so no reload is needed at all in the common case.
 *
 * KNOWN RACES (accepted)
 * ----------------------
 * - snippets/bms-remove-deadline-prod.liquid auto-clicks a remove link during
 *   body render for items past their deadline, navigating to /cart/change. If
 *   that navigation is in flight our writes are superseded; harmless, because
 *   reconciliation is idempotent and re-runs on whatever page we land on. (Note
 *   that removal does not take the parent's charges with it - the orphan sweep
 *   here is what cleans them up on the next load.)
 * - Bold's options.js can write to the cart after window load. We do one pass;
 *   anything Bold adds later is caught on the next cart page view, bounded by
 *   the loop guard.
 */
(function () {
    'use strict';

    var VERSION = '2.7.0';

    /* -------------------------------------------------- config */

    // Variant ids the legacy blanket purge in bms.cart.bundle.js deletes.
    // 44941624067 is the LIVE default personalization charge variant - see the
    // header comment for how that was confirmed.
    var EXCLUDED_VARIANT_IDS = [44941624067];

    // true  - suppress the legacy purge at runtime and reconcile the variants
    //         above like any other charge. This is the fix.
    // false - let the legacy purge keep deleting them, and stand down whenever
    //         one is in the cart (the pre-2.1.0 behaviour, which meant this
    //         script did nothing on any cart using the default charge product).
    // Override for one page load with /cart?pcfix=legacy
    var TAKE_OVER_LEGACY_PURGE = true;

    // Charge products the debug report probes for real prices, so a plan that
    // adds a charge can be checked against actual money before it is trusted.
    // These are the two handles hard-coded in snippets/personalization.liquid.
    var KNOWN_CHARGE_HANDLES = ['charge', 'back-charge'];

    // Every unit of a personalized product needs its own charge, so the target
    // is (options for that charge variant) x (parent line quantity).
    //
    // Confirmed against a live cart on 2026-09-02: hoodie qty 2 with one
    // Left_Sleeve option and _boldVariantQtys = "1" had a single $8.00 charge;
    // the correct cart is 2 charges / $122.00, not 1 / $114.00. Note that
    // _boldVariantQtys is written by product.js from the checked option boxes
    // alone, so it never reflects the parent quantity - the multiplication has
    // to happen here.
    //
    // A useful side effect: when a shopper changes the parent quantity on the
    // cart page (nothing in the theme keeps the charges in step), the next cart
    // load re-syncs the charge count both up and down.
    var MULTIPLY_BY_PARENT_QUANTITY = true;

    // Patch the page in place after a fix instead of reloading it, so the
    // shopper sees one navigation (their own "Update cart" post) rather than a
    // second reload a few seconds later.
    //
    // Only the subtotal and the hidden addon quantity inputs can go stale: the
    // charge rows themselves are hidden by
    // snippets/bold-options-hybrid-cart-item.liquid, and the header count
    // excludes them. So patching those two things is enough.
    //
    // It is only safe while the set of line item KEYS is unchanged - i.e. a
    // quantity moved on lines that already existed. The cart form posts
    // `updates[]` positionally, so if a fix creates or removes a line the DOM's
    // inputs no longer line up with the server's cart and the next "Update
    // cart" click would write quantities to the wrong lines. That case falls
    // back to a reload.
    var SEAMLESS_UPDATE = true;

    // Reconcile at DOMContentLoaded rather than window load. window load waits
    // for every product image on the cart page, which was most of the delay
    // before the second refresh. A second pass still runs at window load to
    // catch anything Bold's options.js writes late; passes are serialised, so
    // they can never both decide to add the same charge.
    var RUN_AT_DOM_READY = true;
    var MAX_PASSES_PER_PAGE = 2;

    // Fold each parent's personalization charge into its rendered Price and
    // TOTAL cells, so the visible rows add up to the subtotal.
    //
    // sections/cart.liquid prints item.price / item.line_price, which cover the
    // parent alone - the charge is a separate, hidden line item. Bold's
    // bold_cart_item_price / bold_cart_item_total theme hooks are meant to
    // rewrite those cells in hybrid mode and are not doing it, so a qty-2 row
    // reads $53.00 / $106.00 while the subtotal reads $122.00.
    //
    // This was invisible while the legacy purge was deleting every charge: the
    // page added up because nothing was being charged. Correcting the charges
    // exposed it.
    var FIX_ROW_PRICES = true;

    // Stop Bold's options.js from rewriting the cart page's prices.
    //
    // sections/cart.liquid carries Bold's anchor spans - bold_cart_item_price,
    // bold_cart_item_total, bold_cart_total, bold_cart_item_properties - and in
    // this store Bold fills them with wrong numbers. Observed 2026-09-02 on a
    // cart holding 2 hoodies + 2 charges (real total $122.00):
    //
    //   row Price cell : $8.00     (the charge alone, not hoodie + charge)
    //   row TOTAL cell : $16.00    (ditto)
    //   subtotal       : $138.00   ($122.00 with the $16.00 charge added again)
    //
    // Bold is double-counting: in v1_variant_mode the option charge IS already a
    // real line item, so adding _boldVariantPrices on top of the cart total
    // counts it twice. The cart itself - and therefore checkout - was right.
    //
    // Renaming the anchor classes (rather than deleting the nodes) leaves Bold's
    // selectors with nothing to find, so the Liquid-rendered figures survive and
    // this file's own corrections stick. Bold's role on the cart page is display
    // only; the charge line items are ordinary line items and are unaffected.
    var NEUTRALISE_BOLD_PRICE_HOOKS = true;

    // Bold can run late. After correcting the display, re-assert it at these
    // delays (ms) in case Bold got there afterwards. decorateRowPrices always
    // computes from cart data, never from what is on screen, so re-asserting is
    // idempotent and cannot drift.
    var DISPLAY_REASSERT_DELAYS = [1200, 3500];

    // Hold the Check out button until the cart is reconciled. Clicking it in the
    // second or two before the first pass finishes checks out a cart with the
    // wrong charges - the whole problem this file exists to fix.
    //
    // A click during that window is not rejected, it is QUEUED: the button shows
    // BUSY_CHECKOUT_LABEL and the click is replayed the moment the cart settles,
    // so the shopper never has to click twice or wonder why nothing happened.
    //
    // The busy class and its failsafe timer live in
    // snippets/gw-personalization-charge-fix.liquid, so the gate is up from the
    // moment the button is parsed and clears itself even if this asset never
    // loads.
    var BLOCK_CHECKOUT_UNTIL_READY = true;
    var BUSY_CHECKOUT_LABEL = 'One moment…';
    var CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"]';

    // Hard stop on remediation attempts per browser session, so a state we
    // cannot converge on can never turn into a reload loop.
    var MAX_FIXES_PER_SESSION = 3;

    var GUARD_KEY = 'gwPcFix';
    var RELOAD_KEY = 'gwPcFixReload';   // who caused the page load we are on
    var DRIFT_KEY = 'gwPcFixDrift';     // did anything overwrite our patch
    var MODE_KEY = 'gwPcFixMode';
    var HISTORY_KEY = 'gwPcFixLog';
    var MAX_HISTORY_PASSES = 8;
    var LOG = '[pcfix]';

    /* -------------------------------------------------- small helpers */

    function isCartPage() {
        return /\/cart\/?$/.test(window.location.pathname);
    }

    function prop(item, name) {
        var props = item && item.properties;
        if (!props) return '';
        var value = props[name];
        if (value === null || value === undefined) return '';
        return String(value);
    }

    function variantIdOf(item) {
        // /cart.js exposes both; `id` is the variant id, `variant_id` is explicit.
        return parseInt(item.variant_id || item.id, 10);
    }

    function parseCsv(value) {
        var out = [];
        var parts = String(value || '').split(',');
        for (var i = 0; i < parts.length; i++) {
            var part = parts[i].replace(/^\s+|\s+$/g, '');
            if (part !== '') out.push(part);
        }
        return out;
    }

    // Visible text of an element, whitespace collapsed.
    function textOf(element) {
        if (!element) return '';
        var text = element.textContent || element.innerText || '';
        return String(text).replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
    }

    function countKeys(object) {
        var total = 0;
        for (var key in object) {
            if (object.hasOwnProperty(key)) total++;
        }
        return total;
    }

    function readStore(key, fallback) {
        try {
            var raw = window.sessionStorage.getItem(key);
            return raw === null ? fallback : JSON.parse(raw);
        } catch (err) {
            return fallback;
        }
    }

    function writeStore(key, value) {
        try {
            window.sessionStorage.setItem(key, JSON.stringify(value));
        } catch (err) { /* private mode - degrades to "no persistence" */ }
    }

    function dropStore(key) {
        try {
            window.sessionStorage.removeItem(key);
        } catch (err) { /* no-op */ }
    }

    function postJson(url, body) {
        return fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        }).then(function (response) {
            return response.text().then(function (text) {
                if (!response.ok) {
                    throw new Error(url + ' -> HTTP ' + response.status + ' ' + text);
                }
                return { status: response.status, body: text };
            });
        });
    }

    /* -------------------------------------------------- checkout gate */

    var checkoutGate = {
        queued: false,          // the shopper clicked while we were still working
        blockedClicks: 0,
        replayed: false,
        reloadPending: false,   // do not release into a page that is about to go
        originalLabel: null
    };

    function isBusy() {
        return /\bgw-pcfix-busy\b/.test(document.documentElement.className || '');
    }

    // The first non-whitespace text node inside an element - the Check out
    // button holds its label as a text node next to an <i> arrow.
    function firstTextNode(element) {
        var children = (element && element.childNodes) || [];

        for (var i = 0; i < children.length; i++) {
            if (children[i].nodeType === 3 && children[i].nodeValue.replace(/\s/g, '') !== '') {
                return children[i];
            }
            if (children[i].nodeType === 1) {
                var nested = firstTextNode(children[i]);
                if (nested) return nested;
            }
        }
        return null;
    }

    function checkoutButton() {
        return document.querySelector(CHECKOUT_SELECTOR);
    }

    function showCheckoutBusy() {
        var button = checkoutButton();
        if (!button) return;

        var label = firstTextNode(button);
        if (label && checkoutGate.originalLabel === null) {
            checkoutGate.originalLabel = label.nodeValue;
            label.nodeValue = BUSY_CHECKOUT_LABEL;
        }
        button.setAttribute('aria-busy', 'true');
    }

    function restoreCheckoutLabel() {
        var button = checkoutButton();
        if (!button) return;

        var label = firstTextNode(button);
        if (label && checkoutGate.originalLabel !== null) {
            label.nodeValue = checkoutGate.originalLabel;
            checkoutGate.originalLabel = null;
        }
        button.removeAttribute('aria-busy');
    }

    function matchesCheckout(node) {
        while (node && node.nodeType === 1) {
            if (node.getAttribute && node.getAttribute('name') === 'checkout') return node;
            node = node.parentNode;
        }
        return null;
    }

    /**
     * Swallow checkout attempts while the cart is still being reconciled, and
     * remember that one was made. Listeners are attached in the capture phase on
     * `document`, so they work regardless of when the button is parsed and
     * whatever else is bound to it.
     */
    function installCheckoutGate() {
        if (!BLOCK_CHECKOUT_UNTIL_READY) return;

        // Without listeners there is no way to hold the button, and throwing
        // here would take the whole reconciliation down with it.
        if (!document.addEventListener) {
            releaseCheckout('cannot install the gate - document.addEventListener unavailable');
            return;
        }

        document.addEventListener('click', function (event) {
            if (!isBusy() || checkoutGate.replayed) return;
            if (!matchesCheckout(event.target)) return;

            event.preventDefault();
            event.stopPropagation();

            checkoutGate.queued = true;
            checkoutGate.blockedClicks++;
            showCheckoutBusy();

            console.info(LOG, 'checkout held until the personalization charges are reconciled; ' +
                'it will continue on its own');
        }, true);

        // Covers Enter-key and script-driven submits that name the checkout
        // button as the submitter.
        document.addEventListener('submit', function (event) {
            if (!isBusy() || checkoutGate.replayed) return;

            var submitter = event.submitter && matchesCheckout(event.submitter);
            if (!submitter) return;

            event.preventDefault();
            event.stopPropagation();

            checkoutGate.queued = true;
            checkoutGate.blockedClicks++;
            showCheckoutBusy();
        }, true);

        // Called by the inline snippet once the busy class is cleared - either by
        // us, or by its own failsafe timer.
        window.__gwPcFixOnRelease = function () {
            restoreCheckoutLabel();

            if (!checkoutGate.queued || checkoutGate.replayed) return;

            checkoutGate.replayed = true;

            var button = checkoutButton();
            if (!button) return;

            // Replay as a real click so the form still posts `checkout`, which
            // form.submit() would drop.
            console.info(LOG, 'cart settled - continuing to checkout');
            button.click();
        };
    }

    function releaseCheckout(why) {
        if (checkoutGate.reloadPending) return;      // the page is about to change
        if (typeof window.__gwPcFixRelease === 'function') window.__gwPcFixRelease(why);
    }

    /* -------------------------------------------------- legacy purge takeover */

    var legacyState = { attempted: false, presenterFound: false, suppressed: false, tooLate: false };

    /**
     * Replace the legacy presenter's init() with a no-op, so its blanket purge
     * of EXCLUDED_VARIANT_IDS never runs and this file can reconcile those
     * variants properly instead.
     *
     * Timing: snippets/bms.cart-init.liquid loads bms.cart.bundle.js with a
     * BLOCKING script tag (so the presenter object exists by the time any
     * deferred script runs) and then registers its DOMContentLoaded listener
     * inline. This file is deferred, so it executes after that listener is
     * registered but before it fires - which is exactly the window in which
     * swapping out init() still works. Hence the include must come after
     * {% render 'bms.cart-init' %} and must keep the `defer` attribute.
     */
    function suppressLegacyPurge() {
        legacyState.attempted = true;
        legacyState.tooLate = document.readyState !== 'loading';
        legacyState.inlineGuard = window.__gwPcFixLegacySuppressed || 'absent';

        var bold = window.BOLD;
        var cart = bold && bold.BMS && bold.BMS.BUILDS && bold.BMS.BUILDS.Cart;
        var presenter = cart && cart.PersonalizationCharge;

        if (!presenter || typeof presenter.init !== 'function') return;

        legacyState.presenterFound = true;

        // The inline guard in snippets/gw-personalization-charge-fix.liquid
        // normally got here first; keep its saved original rather than saving
        // the no-op over the top of it.
        if (!presenter._gwOriginalInit) presenter._gwOriginalInit = presenter.init;

        presenter.init = function () {
            console.info(LOG, 'legacy blanket purge of variant(s) ' + EXCLUDED_VARIANT_IDS.join(', ') +
                ' suppressed; this script reconciles them instead');
        };
        legacyState.suppressed = true;
    }

    /* -------------------------------------------------- mode */

    // Mode is sticky in sessionStorage so it survives the reloads this script
    // triggers - otherwise a debug session would lose the interesting pass.
    function resolveMode() {
        var match = /(?:^|[?&])pcfix=([^&]*)/.exec(window.location.search);
        var stored = readStore(MODE_KEY, null);

        if (match) {
            var flags = decodeURIComponent(match[1]).toLowerCase().split(',');
            var mode = { debug: false, dry: false, keepLegacy: false };

            for (var i = 0; i < flags.length; i++) {
                var flag = flags[i].replace(/^\s+|\s+$/g, '');
                if (flag === 'off' || flag === 'none' || flag === '0') {
                    dropStore(MODE_KEY);
                    dropStore(HISTORY_KEY);
                    return { debug: false, dry: false, keepLegacy: false };
                }
                if (flag === 'debug' || flag === '1' || flag === 'on') mode.debug = true;
                if (flag === 'dry') { mode.dry = true; mode.debug = true; }
                if (flag === 'legacy') mode.keepLegacy = true;
            }

            writeStore(MODE_KEY, mode);
            return mode;
        }

        if (stored) return stored;
        return { debug: false, dry: false, keepLegacy: false };
    }

    /* -------------------------------------------------- classify */

    // Mirrors snippets/bold-options-hybrid-cart-item.liquid exactly, so this
    // file's idea of a hidden charge row is the same as the page's.
    function isLinkedCharge(item) {
        return prop(item, '_boldBuilderId') !== '' && prop(item, '_boldVariantIds') === '';
    }

    function isPersonalizedParent(item) {
        return prop(item, '_boldVariantIds') !== '';
    }

    /* -------------------------------------------------- plan */

    /**
     * Build the reconciliation plan for a cart.
     *
     * Charges are grouped by _boldBuilderId rather than assumed one-per-parent:
     * product.js derives the builder id from `new Date()`, which is only
     * second-precision, so two products added within the same second share an
     * id. Summing the targets of every parent in a group and reconciling
     * against the aggregate makes that collision harmless.
     *
     * Three kinds of demand/supply are tracked, because in practice Bold does
     * not always stamp what it should:
     *   - grouped:   parent and charges share a builder id. The normal case.
     *   - unmatched: parent carries _boldVariantIds but NO builder id, so its
     *                charges cannot be linked to it. Its demand is still real.
     *   - unlinked:  a line item whose variant is one of the charge variants but
     *                which carries no builder id, so the Liquid snippet renders
     *                it as a visible product row. It is real supply. It is
     *                counted, but never added against and never deleted -
     *                deleting a visible row the shopper can see is not this
     *                file's call. Reported loudly instead.
     */
    function buildPlan(cart, options) {
        var opts = options || {};
        var excluded = opts.excludedVariantIds || [];
        var items = (cart && cart.items) || [];
        var groups = {};
        var groupOrder = [];
        var chargeVariantIds = {};      // variant id -> true, learned from parents
        var unmatchedDemand = {};       // parents carrying no builder id at all
        var unlinkedSupply = {};        // charge variants present but unlinkable
        var unlinkedItems = [];
        var classifications = {};       // line key -> label, for the report
        var updates = {};
        var adds = [];
        var notes = [];
        var i;

        function isExcluded(variantId) {
            for (var e = 0; e < excluded.length; e++) {
                if (excluded[e] === variantId) return true;
            }
            return false;
        }

        function group(builderId) {
            if (!groups[builderId]) {
                groups[builderId] = { id: builderId, parents: [], target: {}, lines: {} };
                groupOrder.push(builderId);
            }
            return groups[builderId];
        }

        function addDemand(bucket, target) {
            for (var variantId in target) {
                if (!target.hasOwnProperty(variantId)) continue;
                bucket[variantId] = (bucket[variantId] || 0) + target[variantId];
            }
        }

        // Zip _boldVariantIds with _boldVariantQtys into { variantId: qty },
        // scaled by the parent line quantity (see MULTIPLY_BY_PARENT_QUANTITY).
        // A missing or short _boldVariantQtys means one option per id.
        function targetOf(item) {
            var ids = parseCsv(prop(item, '_boldVariantIds'));
            var qtys = parseCsv(prop(item, '_boldVariantQtys'));
            var units = MULTIPLY_BY_PARENT_QUANTITY ? (item.quantity || 1) : 1;
            var target = {};

            for (var t = 0; t < ids.length; t++) {
                var variantId = parseInt(ids[t], 10);
                if (!variantId) continue;

                chargeVariantIds[variantId] = true;
                if (isExcluded(variantId)) continue;

                var qty = parseInt(qtys[t], 10);
                if (!qty || qty < 0) qty = 1;

                target[variantId] = (target[variantId] || 0) + (qty * units);
            }
            return target;
        }

        // Pass 0 - learn every charge variant id this cart references, so the
        // fallback classification below has something to match against.
        for (i = 0; i < items.length; i++) {
            if (isPersonalizedParent(items[i])) targetOf(items[i]);
        }

        // Pass 1 - classify.
        for (i = 0; i < items.length; i++) {
            var item = items[i];
            var builderId = prop(item, '_boldBuilderId');
            var variantId = variantIdOf(item);

            if (isPersonalizedParent(item)) {
                var target = targetOf(item);
                if (builderId === '') {
                    classifications[item.key] = 'PARENT (no builder id)';
                    addDemand(unmatchedDemand, target);
                } else {
                    classifications[item.key] = 'PARENT';
                    var parentGroup = group(builderId);
                    parentGroup.parents.push(item);
                    addDemand(parentGroup.target, target);
                }
                continue;
            }

            if (isExcluded(variantId)) {
                classifications[item.key] = 'CHARGE (excluded, legacy purge owns it)';
                continue;
            }

            if (isLinkedCharge(item)) {
                classifications[item.key] = 'CHARGE (hidden, linked)';
                var chargeGroup = group(builderId);
                if (!chargeGroup.lines[variantId]) chargeGroup.lines[variantId] = [];
                // Pushed in cart order, so the plan is deterministic.
                chargeGroup.lines[variantId].push(item);
                continue;
            }

            if (chargeVariantIds[variantId]) {
                // A charge variant with no builder id: Bold failed to stamp it,
                // so the cart page is rendering it as a visible product row.
                classifications[item.key] = 'CHARGE (VISIBLE, no builder id)';
                unlinkedSupply[variantId] = (unlinkedSupply[variantId] || 0) + item.quantity;
                unlinkedItems.push(item);
                continue;
            }

            classifications[item.key] = 'product';
        }

        if (unlinkedItems.length) {
            notes.push(unlinkedItems.length + ' charge line item(s) carry no _boldBuilderId, so the cart page is ' +
                'rendering them as VISIBLE product rows. Counted as supply, but not removed - a visible row is the ' +
                'shopper\'s to remove. Bold stamping is failing upstream.');
        }

        // Trim (and optionally top up) one variant of one group to `target`.
        // Returns how much of the target the existing lines already cover.
        function reconcile(grp, variantId, target, mayAdd) {
            var lines = grp.lines[variantId] || [];
            var remaining = target;
            var covered = 0;
            var j;

            for (j = 0; j < lines.length; j++) {
                var line = lines[j];
                var keep = line.quantity < remaining ? line.quantity : remaining;

                remaining -= keep;
                covered += keep;

                if (keep !== line.quantity) updates[line.key] = keep;
            }

            if (mayAdd && remaining > 0) {
                // Never add on top of supply that already exists but could not
                // be linked - that would double-charge the shopper.
                var spare = unlinkedSupply[variantId] || 0;
                if (spare > 0) {
                    var claimed = spare < remaining ? spare : remaining;
                    unlinkedSupply[variantId] = spare - claimed;
                    remaining -= claimed;
                    notes.push('shortfall of ' + claimed + ' for variant ' + variantId +
                        ' covered by an existing unlinked charge instead of a new /cart/add.js');
                }
            }

            if (mayAdd && remaining > 0) {
                adds.push({
                    id: parseInt(variantId, 10),
                    quantity: remaining,
                    properties: { _boldBuilderId: grp.id }
                });
            }

            return covered;
        }

        function variantsOf(grp) {
            var seen = {};
            var list = [];
            var variantId;

            for (variantId in grp.target) {
                if (grp.target.hasOwnProperty(variantId) && !seen[variantId]) {
                    seen[variantId] = true;
                    list.push(variantId);
                }
            }
            for (variantId in grp.lines) {
                if (grp.lines.hasOwnProperty(variantId) && !seen[variantId]) {
                    seen[variantId] = true;
                    list.push(variantId);
                }
            }
            return list;
        }

        // Pass 2 - a parent with no builder id and an unlinked charge of the
        // right variant are almost certainly each other's. Pair them off first.
        for (var demandVariantId in unmatchedDemand) {
            if (!unmatchedDemand.hasOwnProperty(demandVariantId)) continue;

            var demand = unmatchedDemand[demandVariantId];
            var supply = unlinkedSupply[demandVariantId] || 0;
            var paired = demand < supply ? demand : supply;

            if (paired > 0) {
                unmatchedDemand[demandVariantId] = demand - paired;
                unlinkedSupply[demandVariantId] = supply - paired;
            }
        }

        // Pass 3 - groups that have a parent. These own their target outright.
        for (i = 0; i < groupOrder.length; i++) {
            var withParent = groups[groupOrder[i]];
            if (!withParent.parents.length) continue;

            var owned = variantsOf(withParent);
            for (var k = 0; k < owned.length; k++) {
                reconcile(withParent, owned[k], withParent.target[owned[k]] || 0, true);
            }
        }

        // Pass 4 - orphan groups (charges whose builder id matches no parent in
        // the cart). Before deleting them, offset them against the leftover
        // demand of parents that carry no builder id: those charges are most
        // likely theirs, just unlinkable. Never add on behalf of an orphan.
        for (i = 0; i < groupOrder.length; i++) {
            var orphan = groups[groupOrder[i]];
            if (orphan.parents.length) continue;

            for (var orphanVariantId in orphan.lines) {
                if (!orphan.lines.hasOwnProperty(orphanVariantId)) continue;

                var claimable = unmatchedDemand[orphanVariantId] || 0;
                var kept = reconcile(orphan, orphanVariantId, claimable, false);
                unmatchedDemand[orphanVariantId] = claimable - kept;
            }
        }

        return {
            updates: updates,
            adds: adds,
            groups: groups,
            groupOrder: groupOrder,
            classifications: classifications,
            chargeVariantIds: chargeVariantIds,
            unmatchedDemand: unmatchedDemand,
            unlinkedSupply: unlinkedSupply,
            notes: notes
        };
    }

    /* -------------------------------------------------- loop guard */

    function signatureOf(cart) {
        var parts = [];
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
            parts.push(items[i].key + ':' + items[i].quantity);
        }
        parts.sort();
        return (cart && cart.token ? cart.token : '') + '|' + parts.join(',');
    }

    /* -------------------------------------------------- charge product probe */

    /**
     * Look up the real price of the charge products, so the report can state
     * what a planned /cart/add.js actually costs. Debug mode only - it is two
     * extra requests and the reconciliation itself does not need them.
     */
    function probeChargeProducts() {
        var found = {};
        var chain = Promise.resolve();

        KNOWN_CHARGE_HANDLES.forEach(function (handle) {
            chain = chain.then(function () {
                return fetch('/products/' + handle + '.js', { credentials: 'same-origin' }).then(function (response) {
                    if (!response.ok) { found[handle] = { error: 'HTTP ' + response.status }; return; }
                    return response.json().then(function (product) {
                        var variants = [];
                        for (var i = 0; i < (product.variants || []).length; i++) {
                            variants.push({ id: product.variants[i].id, price: product.variants[i].price });
                        }
                        found[handle] = { id: product.id, title: product.title, variants: variants };
                    });
                }, function (err) {
                    found[handle] = { error: String(err && err.message ? err.message : err) };
                });
            });
        });

        return chain.then(function () { return found; });
    }

    // variant id -> price in cents, from whatever the probe resolved.
    function priceIndex(probe) {
        var index = {};
        for (var handle in probe) {
            if (!probe.hasOwnProperty(handle) || !probe[handle] || !probe[handle].variants) continue;
            for (var i = 0; i < probe[handle].variants.length; i++) {
                index[probe[handle].variants[i].id] = probe[handle].variants[i].price;
            }
        }
        return index;
    }

    /* -------------------------------------------------- report */

    function pad(value, width) {
        var text = String(value === null || value === undefined ? '' : value);
        while (text.length < width) text += ' ';
        return text;
    }

    function moneyOf(cents) {
        if (cents === null || cents === undefined) return '';
        return (cents / 100).toFixed(2);
    }

    function environmentLines() {
        var lines = [];
        var shopify = window.Shopify || {};
        var theme = shopify.theme || {};
        var bold = window.BOLD || {};
        var boldCart = (bold.BMS && bold.BMS.BUILDS && bold.BMS.BUILDS.Cart) || {};

        lines.push('url                 : ' + window.location.href);
        lines.push('user agent          : ' + navigator.userAgent);
        lines.push('shop                : ' + (shopify.shop || '(unknown)'));
        lines.push('theme               : ' + (theme.id || '?') + ' "' + (theme.name || '?') + '" role=' + (theme.role || '?'));
        lines.push('currency            : ' + (shopify.currency ? shopify.currency.active : '(unknown)'));
        lines.push('jQuery              : ' + (window.jQuery ? window.jQuery.fn.jquery : '(absent)'));
        lines.push('window.BOLD         : ' + (window.BOLD ? 'present' : 'ABSENT'));
        lines.push('legacy purge script : ' + (boldCart.PersonalizationCharge ? 'present (bms.cart.bundle.js loaded)' : 'ABSENT'));
        lines.push('Bold options.js     : ' + (document.querySelector('script[src*="option.boldapps.net"]') ? 'script tag present' : 'no script tag found'));

        return lines;
    }

    function legacyLines(context) {
        var lines = [];

        lines.push('takeover configured : ' + (TAKE_OVER_LEGACY_PURGE ? 'yes' : 'no') +
            (context.mode && context.mode.keepLegacy ? ' (overridden OFF for this load by ?pcfix=legacy)' : ''));
        lines.push('takeover active     : ' + (context.takeover ? 'YES - legacy purge suppressed' : 'no - legacy purge left running'));
        lines.push('presenter found     : ' + (legacyState.presenterFound ? 'yes' : 'no'));
        lines.push('init() replaced     : ' + (legacyState.suppressed ? 'yes' : 'no'));
        lines.push('inline guard        : ' + (legacyState.inlineGuard || 'not checked') +
            (legacyState.inlineGuard === 'inline' ? '  <- fired during parse, before DOMContentLoaded' : ''));

        // Only a problem if the parse-time inline guard in the snippet did NOT
        // fire; when it did, this script running late is harmless.
        if (legacyState.attempted && legacyState.tooLate && legacyState.inlineGuard !== 'inline') {
            lines.push('WARNING             : document.readyState was "' + document.readyState + '" when the takeover ran');
            lines.push('                      AND the inline guard did not fire, so the legacy purge may already');
            lines.push('                      have run. Check that snippets/gw-personalization-charge-fix.liquid is');
            lines.push('                      rendered after {% render \'bms.cart-init\' %} in templates/cart.liquid.');
        }
        if (context.takeover && !legacyState.suppressed) {
            lines.push('WARNING             : takeover is on but init() was NOT replaced. The legacy purge will');
            lines.push('                      delete variant(s) ' + EXCLUDED_VARIANT_IDS.join(', ') + ' and fight this script.');
        }
        lines.push('excluded from plan  : ' + (context.takeover ? 'none' : EXCLUDED_VARIANT_IDS.join(', ')));

        return lines;
    }

    /**
     * Compare every price the cart page displays against what the cart actually
     * holds. This is the section that answers "the prices on the cart page are
     * wrong" with numbers instead of guesses.
     */
    function priceAuditLines(context) {
        var cart = context.cartAfter || context.cart;
        if (!cart) return ['(no cart data)'];

        var lines = [];
        var items = cart.items || [];
        var i;

        var totals = document.querySelectorAll('.total__price');
        var shownSubtotal = totals.length ? textOf(totals[0]) : '(element not found)';
        var expectedSubtotal = formatMoneyLike(shownSubtotal, cart.total_price);

        lines.push('subtotal shown on page   : ' + shownSubtotal);
        lines.push('cart.total_price         : ' + moneyOf(cart.total_price) +
            (expectedSubtotal ? '  (would render as ' + expectedSubtotal + ')' : ''));
        lines.push('agree?                   : ' +
            (expectedSubtotal === null ? 'CANNOT TELL - money format unparseable'
                : (shownSubtotal === expectedSubtotal ? 'yes' : 'NO - the page is showing a stale or third-party total')));

        var drift = readStore(DRIFT_KEY, null);
        if (drift) {
            lines.push('');
            lines.push('DRIFT DETECTED on an earlier pass: this script wrote ' + drift.weWrote +
                ', then the page showed ' + drift.nowShows);
            lines.push('  => something else is rewriting the subtotal, most likely Bold options.js via the');
            lines.push('     bold_cart_total hook in sections/cart.liquid.');
        }

        // Per-parent: what the "Personalization Charge" line prints, versus what
        // the charge line items in that builder group really cost.
        lines.push('');
        for (i = 0; i < items.length; i++) {
            var parent = items[i];
            var priceCsv = parseCsv(prop(parent, '_boldVariantPrices'));
            if (!priceCsv.length) continue;

            var builderId = prop(parent, '_boldBuilderId');
            var printed = 0;
            var p;

            for (p = 0; p < priceCsv.length; p++) printed += parseInt(priceCsv[p], 10) || 0;

            var charged = 0;
            for (p = 0; p < items.length; p++) {
                if (items[p] === parent) continue;
                if (prop(items[p], '_boldBuilderId') !== builderId) continue;
                if (prop(items[p], '_boldVariantIds') !== '') continue;
                charged += (items[p].price || 0) * items[p].quantity;
            }

            lines.push(parent.product_title + '  (line qty ' + parent.quantity + ', key ' + parent.key + ')');
            lines.push('  cart.liquid prints from _boldVariantPrices : ' + moneyOf(printed) +
                '   [csv "' + prop(parent, '_boldVariantPrices') + '"]');
            lines.push('  actually charged by the charge line items  : ' + moneyOf(charged));
            lines.push('  agree?                                     : ' +
                (printed === charged ? 'yes' : 'NO - off by ' + moneyOf(charged - printed)));

            if (printed !== charged && parent.quantity > 1 && printed * parent.quantity === charged) {
                lines.push('  reason: _boldVariantPrices is PER UNIT and sections/cart.liquid prints it as-is,');
                lines.push('          so a line quantity above 1 always under-prints by the quantity factor.');
            }
        }

        // Whatever the rendered rows currently say, verbatim.
        var rows = document.querySelectorAll('.cart-products__item');
        lines.push('');
        lines.push('rendered rows:');
        for (i = 0; i < rows.length; i++) {
            lines.push('  [' + (i + 1) + '] variant ' + rows[i].getAttribute('data-variant-id'));
            lines.push('      unit price cell : ' + textOf(rows[i].querySelector('.product-list__item--price')));
            lines.push('      line total cell : ' + textOf(rows[i].querySelector('.product-list__item--total-price')));

            var extras = rows[i].querySelectorAll('.personalization-list__item');
            for (var e = 0; e < extras.length; e++) {
                lines.push('      personalization : ' + textOf(extras[e]));
            }
        }

        return lines;
    }

    function chargeProductLines(context) {
        var lines = [];
        var probe = context.probe;
        var handle;

        if (!probe) return ['(not probed)'];

        for (handle in probe) {
            if (!probe.hasOwnProperty(handle)) continue;

            var entry = probe[handle];
            if (!entry || entry.error) {
                lines.push(pad(handle, 14) + ': not found (' + (entry ? entry.error : 'no data') + ')');
                continue;
            }

            lines.push(pad(handle, 14) + ': "' + entry.title + '" product id ' + entry.id);
            for (var i = 0; i < entry.variants.length; i++) {
                lines.push('                variant ' + entry.variants[i].id + '  price ' + moneyOf(entry.variants[i].price));
            }
        }

        var index = priceIndex(probe);
        var chargeIds = context.plan ? Object.keys(context.plan.chargeVariantIds) : [];

        lines.push('');
        for (var c = 0; c < chargeIds.length; c++) {
            var known = index[chargeIds[c]];
            lines.push('charge variant ' + chargeIds[c] + ' -> ' +
                (known === undefined ? 'price UNKNOWN (not one of the probed handles)' : moneyOf(known) + ' each'));
        }

        // sections/cart.liquid prints the parent's _boldVariantPrices through the
        // `money` filter, which expects cents. If the product page wrote something
        // in a different unit, the cart shows a price that never matches the real
        // charge line item - worth catching here rather than in a support ticket.
        var items = (context.cart && context.cart.items) || [];
        for (var p = 0; p < items.length; p++) {
            var written = parseCsv(prop(items[p], '_boldVariantPrices'));
            var ids = parseCsv(prop(items[p], '_boldVariantIds'));
            if (!written.length || !ids.length) continue;

            var actual = index[parseInt(ids[0], 10)];
            if (actual === undefined) continue;

            var claimed = parseInt(written[0], 10);
            if (claimed !== actual) {
                lines.push('');
                lines.push('PRICE MISMATCH on ' + items[p].key);
                lines.push('  _boldVariantPrices says ' + claimed + ' cents (' + moneyOf(claimed) +
                    ') - this is what the cart page prints as "Personalization Charge"');
                lines.push('  the charge variant actually costs ' + actual + ' cents (' + moneyOf(actual) +
                    ') - this is what the shopper is really billed');
            }
        }

        if (context.plan && context.plan.adds.length) {
            var total = 0;
            var unknown = false;
            for (var a = 0; a < context.plan.adds.length; a++) {
                var add = context.plan.adds[a];
                if (index[add.id] === undefined) { unknown = true; continue; }
                total += index[add.id] * add.quantity;
            }
            lines.push('');
            lines.push('MONEY IMPACT of the planned add(s): ' + moneyOf(total) +
                (unknown ? ' + some at unknown prices' : ''));
        }

        return lines;
    }

    function domLines() {
        var lines = [];
        var rows = document.querySelectorAll('.cart-products__item');
        var hidden = document.querySelectorAll('input.cart__product-qty[name="updates[]"]');
        var visibleQty = document.querySelectorAll('input.custom-number-input[name="updates[]"]');
        var i;

        lines.push('visible cart rows (.cart-products__item)      : ' + rows.length);
        lines.push('visible qty inputs (.custom-number-input)     : ' + visibleQty.length);
        lines.push('hidden addon qty inputs (.cart__product-qty)  : ' + hidden.length);

        for (i = 0; i < rows.length; i++) {
            lines.push('  row ' + (i + 1) + ' data-variant-id=' + rows[i].getAttribute('data-variant-id'));
        }
        for (i = 0; i < hidden.length; i++) {
            lines.push('  hidden addon input data-id=' + hidden[i].getAttribute('data-id') + ' value=' + hidden[i].value);
        }

        return lines;
    }

    function itemTableLines(cart, plan) {
        var lines = [];
        var items = (cart && cart.items) || [];

        lines.push(pad('#', 3) + pad('key', 42) + pad('variant id', 14) + pad('qty', 5) +
            pad('line $', 10) + pad('classification', 40) + 'title');
        lines.push(new Array(140).join('-'));

        for (var i = 0; i < items.length; i++) {
            var item = items[i];
            lines.push(pad(i + 1, 3) + pad(item.key, 42) + pad(variantIdOf(item), 14) +
                pad(item.quantity, 5) + pad(moneyOf(item.line_price), 10) +
                pad(plan.classifications[item.key] || '?', 40) +
                (item.product_title || item.title || ''));
        }

        return lines;
    }

    function propertyLines(cart, plan) {
        var lines = [];
        var items = (cart && cart.items) || [];

        for (var i = 0; i < items.length; i++) {
            var item = items[i];
            var label = plan.classifications[item.key] || '?';
            if (label === 'product') continue;

            lines.push('[' + (i + 1) + '] ' + (item.product_title || item.title) +
                '  key=' + item.key + '  variant=' + variantIdOf(item) + '  qty=' + item.quantity);
            lines.push('     handle=' + (item.handle || '?') + '  ' + label);

            var props = item.properties || {};
            var names = [];
            for (var name in props) {
                if (props.hasOwnProperty(name)) names.push(name);
            }
            names.sort();

            if (!names.length) {
                lines.push('     (no properties)');
            } else {
                for (var n = 0; n < names.length; n++) {
                    lines.push('     ' + names[n] + ' = ' + JSON.stringify(props[names[n]]));
                }
            }
            lines.push('');
        }

        return lines.length ? lines : ['(no personalization-related line items in this cart)'];
    }

    function groupLines(plan) {
        var lines = [];
        var variantId;

        if (!plan.groupOrder.length) return ['(no builder-id groups in this cart)'];

        for (var i = 0; i < plan.groupOrder.length; i++) {
            var grp = plan.groups[plan.groupOrder[i]];
            var parentKeys = [];

            for (var p = 0; p < grp.parents.length; p++) {
                parentKeys.push(grp.parents[p].key + ' (line qty ' + grp.parents[p].quantity + ')');
            }

            lines.push('builder id: "' + grp.id + '"');
            lines.push('  parents : ' + (parentKeys.length ? parentKeys.join(', ') : 'NONE -> orphan group'));

            // Spell out the arithmetic, so a wrong count can be read straight
            // off the report instead of inferred.
            for (var q = 0; q < grp.parents.length; q++) {
                var parentItem = grp.parents[q];
                lines.push('    target from ' + parentItem.key + ': _boldVariantQtys="' +
                    prop(parentItem, '_boldVariantQtys') + '" x line qty ' +
                    (MULTIPLY_BY_PARENT_QUANTITY ? parentItem.quantity : 1) +
                    ' (ids "' + prop(parentItem, '_boldVariantIds') + '")');
            }

            for (variantId in grp.target) {
                if (!grp.target.hasOwnProperty(variantId)) continue;

                var lineItems = grp.lines[variantId] || [];
                var actual = 0;
                var detail = [];

                for (var l = 0; l < lineItems.length; l++) {
                    actual += lineItems[l].quantity;
                    detail.push(lineItems[l].key + '(qty ' + lineItems[l].quantity + ')');
                }

                lines.push('  variant ' + variantId + ': target ' + grp.target[variantId] +
                    ', actual ' + actual + (actual === grp.target[variantId] ? '  OK' : '  MISMATCH'));
                lines.push('    lines: ' + (detail.length ? detail.join(', ') : 'none'));
            }

            for (variantId in grp.lines) {
                if (!grp.lines.hasOwnProperty(variantId)) continue;
                if (grp.target.hasOwnProperty(variantId)) continue;

                var extras = grp.lines[variantId];
                var extraTotal = 0;
                var extraDetail = [];

                for (var x = 0; x < extras.length; x++) {
                    extraTotal += extras[x].quantity;
                    extraDetail.push(extras[x].key + '(qty ' + extras[x].quantity + ')');
                }

                lines.push('  variant ' + variantId + ': target 0, actual ' + extraTotal + '  NOT EXPECTED');
                lines.push('    lines: ' + extraDetail.join(', '));
            }

            lines.push('');
        }

        return lines;
    }

    function planLines(plan) {
        var lines = [];
        var key;

        if (!countKeys(plan.updates)) {
            lines.push('quantity updates : none');
        } else {
            lines.push('quantity updates (line key -> new qty):');
            for (key in plan.updates) {
                if (!plan.updates.hasOwnProperty(key)) continue;
                lines.push('  ' + key + ' -> ' + plan.updates[key] + (plan.updates[key] === 0 ? '   (remove)' : '   (trim)'));
            }
        }

        if (!plan.adds.length) {
            lines.push('adds             : none');
        } else {
            lines.push('adds (POST /cart/add.js):');
            for (var i = 0; i < plan.adds.length; i++) {
                lines.push('  ' + JSON.stringify(plan.adds[i]));
            }
        }

        return lines;
    }

    function diagnosisLines(context) {
        var lines = [];
        var plan = context.plan;

        if (context.error) {
            lines.push('* FAILED: ' + context.error);
            return lines;
        }

        if (context.legacyMatches && context.legacyMatches.length) {
            lines.push('* The legacy purge in bms.cart.bundle.js owns variant(s) ' + EXCLUDED_VARIANT_IDS.join(', ') +
                ' and this cart contains ' + context.legacyMatches.length + ' such line item(s):');
            for (var m = 0; m < context.legacyMatches.length; m++) {
                lines.push('    ' + context.legacyMatches[m]);
            }
            if (context.stoodDown) {
                lines.push('  => THIS SCRIPT DID NOTHING THIS LOAD. If those titles above are your live');
                lines.push('     personalization charge product, the legacy script is deleting every charge on');
                lines.push('     every cart load and nothing here can help until it is dealt with. Confirm with');
                lines.push('     /cart?pcfix=dry,nolegacy - that shows what this script WOULD do if it took over.');
            }
        }

        if (!plan) return lines;

        if (!plan.groupOrder.length && !countKeys(plan.unmatchedDemand)) {
            lines.push('* No personalization data on any line item in this cart. Either nothing personalized was');
            lines.push('  added, or the product page never stamped _boldVariantIds (check the PROPERTIES section).');
        }

        var parentCount = 0;
        var chargeCount = 0;
        for (var g = 0; g < plan.groupOrder.length; g++) {
            var grp = plan.groups[plan.groupOrder[g]];
            parentCount += grp.parents.length;
            for (var v in grp.lines) {
                if (grp.lines.hasOwnProperty(v)) chargeCount += grp.lines[v].length;
            }
        }
        lines.push('* ' + parentCount + ' personalized parent line item(s), ' + chargeCount + ' linked charge line item(s), ' +
            plan.groupOrder.length + ' builder-id group(s).');

        if (parentCount && !chargeCount) {
            lines.push('* Parents exist but NO linked charge line items were found. Bold options.js is not creating');
            lines.push('  them, or is stamping a property name other than _boldBuilderId - check PROPERTIES below.');
        }

        for (var i = 0; i < plan.notes.length; i++) lines.push('* ' + plan.notes[i]);

        if (context.guardBlocked) lines.push('* LOOP GUARD blocked this pass: ' + context.guardBlocked);
        if (context.dryRun) lines.push('* DRY RUN - nothing was written to the cart.');

        if (context.applied && context.patched) {
            lines.push('* Applied ' + context.applied + ' change(s) and patched the subtotal in place - no reload.');
        } else if (context.applied) {
            lines.push('* Applied ' + context.applied + ' change(s), then reloaded because the set of line item');
            lines.push('  keys changed (a charge line was created or removed), so the cart form\'s positional');
            lines.push('  updates[] inputs had to be re-rendered by the server.');
        }
        if (context.applyError) lines.push('* APPLY FAILED: ' + context.applyError + ' (cart left as-is, no reload)');

        if (!context.error && plan && !countKeys(plan.updates) && !plan.adds.length && !context.stoodDown) {
            lines.push('* Nothing to change: the cart already matches what the parent line items recorded.');
        }

        return lines;
    }

    function buildReport(context) {
        var lines = [];
        var history = readStore(HISTORY_KEY, []);

        function section(title, body) {
            lines.push('');
            lines.push('--- ' + title + ' ' + new Array(Math.max(2, 76 - title.length)).join('-'));
            for (var i = 0; i < body.length; i++) lines.push(body[i]);
        }

        lines.push('=== GRADWEAR PERSONALIZATION CHARGE FIX - DEBUG REPORT =======================');
        lines.push('script version : ' + VERSION);
        lines.push('generated      : ' + new Date().toISOString());
        lines.push('mode           : ' + (context.dryRun ? 'dry (no writes)' : 'debug (writes enabled)') +
            (context.mode && context.mode.keepLegacy ? ' + legacy (takeover off)' : ''));
        lines.push('pass           : ' + (history.length + 1) + ' this session');
        lines.push('triggered by   : ' + context.trigger);
        lines.push('page update    : ' + (context.applied === undefined ? 'nothing applied' :
            (context.patched ? 'patched in place, NO reload' : 'RELOAD REQUIRED')) +
            (SEAMLESS_UPDATE ? '' : '  (seamless mode is off)'));
        if (context.patchReason) {
            lines.push('  reason       : ' + context.patchReason);
        }
        lines.push('checkout gate  : ' + (!BLOCK_CHECKOUT_UNTIL_READY ? 'disabled in the config'
            : (isBusy() ? 'HELD (cart not settled yet)'
                : 'released' + (window.__gwPcFixReleasedBecause ? ' - ' + window.__gwPcFixReleasedBecause : ''))) +
            (checkoutGate.blockedClicks
                ? '; ' + checkoutGate.blockedClicks + ' early checkout click(s) queued' +
                  (checkoutGate.replayed ? ' and replayed' : ' and still waiting')
                : ''));
        lines.push('bold hooks     : ' + (context.boldHooks ? context.boldHooks.reason : 'not attempted this pass'));
        lines.push('subtotal       : ' + (context.subtotal ? context.subtotal.reason : 'not repainted this pass'));
        lines.push('row prices     : ' + (context.rowPrices ? context.rowPrices.reason : 'not attempted this pass'));
        lines.push('this page load : ' + (context.previousReload
            ? 'CAUSED BY THIS SCRIPT reloading at ' + context.previousReload.at +
              '\n                 because: ' + context.previousReload.reason
            : 'not caused by this script (shopper navigation, or another script reloaded)'));
        lines.push('loop guard     : ' + JSON.stringify(readStore(GUARD_KEY, {})));

        section('ENVIRONMENT', environmentLines());
        section('LEGACY PURGE TAKEOVER', legacyLines(context));
        section('DIAGNOSIS', diagnosisLines(context));

        if (context.cart) {
            section('CART SUMMARY', [
                'token            : ' + context.cart.token,
                'item_count       : ' + context.cart.item_count,
                'line item count  : ' + ((context.cart.items || []).length),
                'items_subtotal   : ' + moneyOf(context.cart.items_subtotal_price),
                'total_price      : ' + moneyOf(context.cart.total_price),
                'attributes       : ' + JSON.stringify(context.cart.attributes),
                'note             : ' + JSON.stringify(context.cart.note)
            ]);
        }

        if (context.cart && context.plan) {
            section('LINE ITEMS', itemTableLines(context.cart, context.plan));
            section('PROPERTIES (personalization-related items only)', propertyLines(context.cart, context.plan));
            section('BUILDER ID GROUPS (target vs actual)', groupLines(context.plan));
            section('LEFTOVER DEMAND / SUPPLY', [
                'demand from parents with no builder id : ' + JSON.stringify(context.plan.unmatchedDemand),
                'unlinked charge supply still spare     : ' + JSON.stringify(context.plan.unlinkedSupply),
                'charge variant ids seen in this cart   : ' + Object.keys(context.plan.chargeVariantIds).join(', ')
            ]);
            section('PLAN', planLines(context.plan));
            section('CHARGE PRODUCTS (real prices)', chargeProductLines(context));
            section('PRICE AUDIT (page vs cart)', priceAuditLines(context));
        }

        section('RENDERED DOM', domLines());

        if (history.length) {
            var historyLines = [];
            for (var h = 0; h < history.length; h++) {
                historyLines.push('pass ' + (h + 1) + ' @ ' + history[h].at + ' : ' + history[h].summary);
            }
            section('EARLIER PASSES THIS SESSION', historyLines);
        }

        if (context.cart) {
            section('RAW /cart.js', [JSON.stringify(context.cart, null, 2)]);
        }

        lines.push('');
        lines.push('=== END REPORT ===============================================================');

        return lines.join('\n');
    }

    /* -------------------------------------------------- debug panel */

    function showPanel(text) {
        var existing = document.getElementById('gw-pcfix-panel');
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

        var panel = document.createElement('div');
        panel.id = 'gw-pcfix-panel';
        panel.setAttribute('style', [
            'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483647',
            'background:#12141a', 'color:#e6e6e6', 'border-top:3px solid #4ea1ff',
            'font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
            'box-shadow:0 -6px 24px rgba(0,0,0,.45)', 'padding:10px 12px'
        ].join(';'));

        var bar = document.createElement('div');
        bar.setAttribute('style', 'display:flex;gap:8px;align-items:center;margin-bottom:8px;flex-wrap:wrap');

        var title = document.createElement('strong');
        title.appendChild(document.createTextNode('pcfix debug report v' + VERSION));
        title.setAttribute('style', 'color:#4ea1ff;margin-right:auto');
        bar.appendChild(title);

        var area = document.createElement('textarea');
        area.readOnly = true;
        area.value = text;
        area.setAttribute('style', [
            'width:100%', 'height:46vh', 'box-sizing:border-box', 'resize:vertical',
            'background:#0a0c10', 'color:#d8dee9', 'border:1px solid #2a2f3a', 'padding:8px',
            'font:12px/1.45 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
            'white-space:pre', 'overflow:auto'
        ].join(';'));

        function button(label, handler) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.appendChild(document.createTextNode(label));
            btn.setAttribute('style', [
                'background:#22304a', 'color:#e6e6e6', 'border:1px solid #3a4c6e',
                'padding:5px 12px', 'cursor:pointer', 'font:inherit', 'border-radius:3px'
            ].join(';'));
            btn.onclick = handler;
            return btn;
        }

        var copyButton = button('Copy report', function () {
            area.focus();
            area.select();

            function done(ok) {
                copyButton.firstChild.nodeValue = ok ? 'Copied!' : 'Press Ctrl+C';
                window.setTimeout(function () { copyButton.firstChild.nodeValue = 'Copy report'; }, 2000);
            }

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(function () { done(true); }, function () { done(false); });
                return;
            }
            try { done(document.execCommand('copy')); } catch (err) { done(false); }
        });

        bar.appendChild(copyButton);
        bar.appendChild(button('Select all', function () { area.focus(); area.select(); }));
        bar.appendChild(button('Turn debug off', function () {
            dropStore(MODE_KEY);
            dropStore(HISTORY_KEY);
            window.location.href = window.location.pathname;
        }));
        bar.appendChild(button('Hide', function () {
            if (panel.parentNode) panel.parentNode.removeChild(panel);
        }));

        panel.appendChild(bar);
        panel.appendChild(area);

        (document.body || document.documentElement).appendChild(panel);
    }

    /* -------------------------------------------------- seamless page patch */

    /**
     * Reformat `cents` the way `sample` is already formatted, so the subtotal
     * keeps the shop's money format without this file having to know it (Liquid's
     * `money` filter is not reachable from JS). Returns null if the sample can't
     * be parsed, which makes the caller fall back to a reload rather than print
     * a wrong-looking price.
     */
    function formatMoneyLike(sample, cents) {
        var match = /^(\D*)([\d.,\s]+?)(\D*)$/.exec(String(sample).replace(/ /g, ' ').replace(/^\s+|\s+$/g, ''));
        if (!match) return null;

        var digits = match[2].replace(/\s/g, '');
        if (!/\d/.test(digits)) return null;

        // Whichever separator sits two places from the end is the decimal one.
        var decimalSeparator = /[.,]\d{2}$/.test(digits) ? digits.charAt(digits.length - 3) : '.';
        var groupSeparator = decimalSeparator === ',' ? '.' : ',';
        var hasGroups = digits.indexOf(groupSeparator) !== -1 || Math.abs(cents) >= 100000;

        var whole = String(Math.floor(Math.abs(cents) / 100));
        var fraction = String(Math.abs(cents) % 100);
        while (fraction.length < 2) fraction = '0' + fraction;

        if (hasGroups) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);

        return match[1] + (cents < 0 ? '-' : '') + whole + decimalSeparator + fraction + match[3];
    }

    /**
     * Find the text node that actually holds the money inside an element.
     *
     * It is NOT always a direct child: Bold's options.js rewrites these cells
     * and wraps the figure in its own element, which is why an earlier version
     * of this file silently failed to patch anything. So walk descendants and
     * take the last text node containing a digit, skipping Bold's hidden anchor
     * spans (which are empty markers, not content).
     */
    function findMoneyTextNode(element) {
        var found = null;

        function walk(node) {
            var children = node.childNodes || [];

            for (var i = 0; i < children.length; i++) {
                var child = children[i];

                if (child.nodeType === 3) {
                    if (/\d/.test(child.nodeValue)) found = child;
                } else if (child.nodeType === 1) {
                    if (!/Bold-theme-hook/.test(child.className || '')) walk(child);
                }
            }
        }

        walk(element);
        return found;
    }

    // Work out what a money element should say, without writing it, so callers
    // can validate every element before mutating any of them. Returns null if
    // there is no money text to be found or its format cannot be read.
    function planMoneyText(element, cents) {
        var textNode = findMoneyTextNode(element);
        if (!textNode) return null;

        var formatted = formatMoneyLike(textNode.nodeValue, cents);
        if (formatted === null) return null;

        return { node: textNode, value: formatted };
    }

    /**
     * Make the displayed subtotal equal cart.total_price. Needed even when no
     * fix was applied, because Bold's bold_cart_total hook inflates it - it adds
     * _boldVariantPrices on top of a cart total that already contains the charge
     * line items, so the charge gets counted twice.
     */
    function repaintSubtotal(cart) {
        var totals = document.querySelectorAll('.total__price');
        if (!totals.length) return { applied: 0, reason: 'no .total__price element on the page' };

        var plans = [];
        var i;

        for (i = 0; i < totals.length; i++) {
            var planned = planMoneyText(totals[i], cart.total_price);
            if (!planned) {
                return {
                    applied: 0,
                    reason: 'no readable money text in the subtotal (' + JSON.stringify(textOf(totals[i])) + ')'
                };
            }
            plans.push(planned);
        }

        var changed = 0;
        for (i = 0; i < plans.length; i++) {
            if (plans[i].node.nodeValue !== plans[i].value) changed++;
            plans[i].node.nodeValue = plans[i].value;
        }

        return {
            applied: changed,
            reason: changed ? 'subtotal corrected to ' + plans[0].value + ' (cart.total_price)'
                : 'subtotal already matched cart.total_price'
        };
    }

    function keySignature(cart) {
        var keys = [];
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) keys.push(items[i].key);
        keys.sort();
        return keys.join('|');
    }

    function keyList(cart) {
        var keys = [];
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) keys.push(items[i].key);
        return keys;
    }

    function missingFrom(a, b) {
        var out = [];
        for (var i = 0; i < a.length; i++) {
            if (b.indexOf(a[i]) === -1) out.push(a[i]);
        }
        return out;
    }

    /**
     * Bring the rendered page in line with `after` without navigating.
     * Returns { patched: boolean, reason: string } - the reason is what the
     * debug report prints, so a reload is never unexplained.
     */
    function patchPage(before, after) {
        if (!SEAMLESS_UPDATE) {
            return { patched: false, reason: 'SEAMLESS_UPDATE is turned off in the config' };
        }

        var added = missingFrom(keyList(after), keyList(before));
        var removed = missingFrom(keyList(before), keyList(after));
        var i;

        // A NEW line has to be reloaded. The cart form posts updates[]
        // positionally, and placing an input for a line we have not seen
        // rendered means guessing where the server will order it - not a guess
        // worth making when a wrong guess writes quantities to the wrong lines.
        if (added.length) {
            return {
                patched: false,
                reason: 'new line item(s) created (' + added.join(', ') + '), so the server has to ' +
                    're-render the positional updates[] inputs'
            };
        }

        // A REMOVED line is different: deleting that line's hidden input keeps
        // the remaining inputs in a correct 1:1 order with the remaining cart
        // items, so alignment is provably preserved and no reload is needed.
        // Only valid for hidden charge rows - a visible product row would have
        // to disappear from the page, which is not this script's business.
        var inputsToRemove = [];
        for (i = 0; i < removed.length; i++) {
            var doomed = document.querySelector('input.cart__product-qty[data-id="' + removed[i] + '"]');
            if (!doomed) {
                return {
                    patched: false,
                    reason: 'line ' + removed[i] + ' was removed but has no hidden addon input in the DOM ' +
                        '(it was a visible row), so the page cannot be corrected in place'
                };
            }
            inputsToRemove.push(doomed);
        }

        var totals = document.querySelectorAll('.total__price');
        if (!totals.length) {
            return { patched: false, reason: 'no .total__price element on the page to patch' };
        }

        // Validate every money element before writing to any of them, so a
        // half-patched page is impossible.
        var moneyWrites = [];
        for (i = 0; i < totals.length; i++) {
            var planned = planMoneyText(totals[i], after.total_price);
            if (!planned) {
                return {
                    patched: false,
                    reason: 'could not read the money format of the displayed subtotal ' +
                        JSON.stringify(textOf(totals[i])) + ', so it was left for the server to render'
                };
            }
            moneyWrites.push(planned);
        }

        /* ---- everything validated; now mutate ---- */

        for (i = 0; i < moneyWrites.length; i++) {
            moneyWrites[i].node.nodeValue = moneyWrites[i].value;
        }

        for (i = 0; i < inputsToRemove.length; i++) {
            if (inputsToRemove[i].parentNode) {
                inputsToRemove[i].parentNode.removeChild(inputsToRemove[i]);
            }
        }

        // Keep the surviving hidden addon inputs in step, so the cart form still
        // posts the right quantities for the charge lines.
        var items = (after && after.items) || [];
        var resynced = 0;
        for (i = 0; i < items.length; i++) {
            var input = document.querySelector('input.cart__product-qty[data-id="' + items[i].key + '"]');
            if (input) {
                input.value = items[i].quantity;
                resynced++;
            }
        }

        return {
            patched: true,
            reason: 'subtotal patched, ' + resynced + ' hidden addon input(s) resynced' +
                (inputsToRemove.length ? ', ' + inputsToRemove.length + ' removed-line input(s) deleted' : '')
        };
    }

    /**
     * Rename Bold's cart-page anchor classes so its options.js can no longer
     * find them, leaving the Liquid-rendered prices (and this file's
     * corrections) in place. See NEUTRALISE_BOLD_PRICE_HOOKS for the numbers
     * that made this necessary.
     */
    function neutraliseBoldPriceHooks() {
        if (!NEUTRALISE_BOLD_PRICE_HOOKS) {
            return { disabled: 0, reason: 'NEUTRALISE_BOLD_PRICE_HOOKS is turned off in the config' };
        }

        var anchors = document.querySelectorAll(
            '.bold_cart_item_price, .bold_cart_item_total, .bold_cart_total, .bold_cart_item_properties'
        );
        var disabled = 0;

        for (var i = 0; i < anchors.length; i++) {
            var className = anchors[i].className || '';
            if (className.indexOf('gw-disabled-') !== -1) continue;

            anchors[i].className = className.replace(
                /\b(bold_cart_item_price|bold_cart_item_total|bold_cart_total|bold_cart_item_properties)\b/g,
                'gw-disabled-$1'
            );
            disabled++;
        }

        return {
            disabled: disabled,
            reason: disabled
                ? disabled + ' Bold price hook(s) disabled so they stop rewriting the cart prices'
                : (anchors.length ? 'Bold price hooks already disabled' : 'no Bold price hooks found on the page')
        };
    }

    /**
     * How many cents of personalization charge belong to each parent line,
     * keyed by the parent's line item key, computed from the charge line items
     * that are actually in the cart.
     *
     * A builder group normally holds one parent. It can hold more, because
     * product.js derives the builder id from a second-precision `new Date()`;
     * in that case the group's charges are split across its parents in
     * proportion to what each one asked for.
     */
    function chargeCentsByParent(cart) {
        var items = (cart && cart.items) || [];
        var groups = {};
        var byParentKey = {};
        var i;

        function group(builderId) {
            if (!groups[builderId]) groups[builderId] = { parents: [], charged: 0 };
            return groups[builderId];
        }

        for (i = 0; i < items.length; i++) {
            var builderId = prop(items[i], '_boldBuilderId');
            if (builderId === '') continue;

            if (isLinkedCharge(items[i])) {
                group(builderId).charged += (items[i].price || 0) * items[i].quantity;
            } else if (prop(items[i], '_boldVariantIds') !== '') {
                group(builderId).parents.push(items[i]);
            }
        }

        for (var id in groups) {
            if (!groups.hasOwnProperty(id)) continue;

            var entry = groups[id];
            if (!entry.parents.length || !entry.charged) continue;

            if (entry.parents.length === 1) {
                byParentKey[entry.parents[0].key] = entry.charged;
                continue;
            }

            // Shared builder id: weight by each parent's own option count x qty.
            var weights = [];
            var totalWeight = 0;
            for (i = 0; i < entry.parents.length; i++) {
                var qtys = parseCsv(prop(entry.parents[i], '_boldVariantQtys'));
                var options = 0;
                for (var q = 0; q < qtys.length; q++) options += parseInt(qtys[q], 10) || 1;
                if (!options) options = 1;

                var weight = options * (MULTIPLY_BY_PARENT_QUANTITY ? (entry.parents[i].quantity || 1) : 1);
                weights.push(weight);
                totalWeight += weight;
            }

            var assigned = 0;
            for (i = 0; i < entry.parents.length; i++) {
                // Give the last parent the remainder, so the split is exact.
                var share = (i === entry.parents.length - 1)
                    ? entry.charged - assigned
                    : Math.round(entry.charged * weights[i] / totalWeight);
                assigned += share;
                byParentKey[entry.parents[i].key] = share;
            }
        }

        return byParentKey;
    }

    /**
     * Rewrite the rendered Price and TOTAL cells so each parent row shows what
     * it really costs, charge included. Runs on every pass, not just when a fix
     * was applied - a steady-state cart still renders these cells short.
     *
     * Rows are matched to cart lines POSITIONALLY (the Liquid loop renders
     * non-charge items in cart order) and then cross-checked against each row's
     * data-variant-id. If any row disagrees, nothing is written at all - a wrong
     * price is worse than a short one.
     */
    function decorateRowPrices(cart) {
        if (!FIX_ROW_PRICES) return { applied: 0, reason: 'FIX_ROW_PRICES is turned off in the config' };

        var items = (cart && cart.items) || [];
        var visible = [];
        var i;

        for (i = 0; i < items.length; i++) {
            if (!isLinkedCharge(items[i])) visible.push(items[i]);
        }

        var rows = document.querySelectorAll('.cart-products__item');
        if (!rows.length) return { applied: 0, reason: 'no rendered cart rows' };

        if (rows.length !== visible.length) {
            return {
                applied: 0,
                reason: 'the page shows ' + rows.length + ' row(s) but the cart holds ' + visible.length +
                    ' visible line(s), so rows cannot be matched to cart lines safely'
            };
        }

        for (i = 0; i < rows.length; i++) {
            var shown = rows[i].getAttribute('data-variant-id');
            if (shown && String(shown) !== String(variantIdOf(visible[i]))) {
                return {
                    applied: 0,
                    reason: 'row ' + (i + 1) + ' renders variant ' + shown + ' but cart line ' + (i + 1) +
                        ' is variant ' + variantIdOf(visible[i]) + ', so the mapping is not trustworthy'
                };
            }
        }

        var charges = chargeCentsByParent(cart);
        var writes = [];
        var needed = 0;
        var unwritable = 0;

        // Validate every cell before writing any of them.
        for (i = 0; i < rows.length; i++) {
            var line = visible[i];
            var extra = charges[line.key] || 0;
            if (!extra) continue;

            needed++;

            // Both money cells are an inner .product-list__price; note the
            // TOTAL cell has no .product-list__holder wrapper, unlike Price.
            var unitCell = rows[i].querySelector('.product-list__item--price .product-list__price');
            var totalCell = rows[i].querySelector('.product-list__item--total-price .product-list__price');

            var quantity = line.quantity || 1;
            var unitPlan = unitCell ? planMoneyText(unitCell, line.price + Math.round(extra / quantity)) : null;
            var totalPlan = totalCell ? planMoneyText(totalCell, line.line_price + extra) : null;

            if (unitPlan) writes.push(unitPlan); else unwritable++;
            if (totalPlan) writes.push(totalPlan); else unwritable++;
        }

        for (i = 0; i < writes.length; i++) writes[i].node.nodeValue = writes[i].value;

        var reason;
        if (writes.length) {
            reason = writes.length + ' price cell(s) rewritten to include the charge';
            if (unwritable) reason += ', but ' + unwritable + ' cell(s) had no readable money text';
        } else if (!needed) {
            reason = 'no row carries a charge to fold in';
        } else {
            // This is what silently happened before: Bold had wrapped the money
            // in its own markup, so there was no text node to write to.
            reason = needed + ' row(s) need a charge folded in, but no money text could be found in their ' +
                'price cells - something has replaced the cell markup';
        }

        return { applied: writes.length, needed: needed, unwritable: unwritable, reason: reason };
    }

    /**
     * Re-apply the row prices after Bold has had time to run, in case it
     * rewrote the cells behind us. decorateRowPrices computes from cart data
     * rather than from what is on screen, so this is idempotent.
     */
    function scheduleDisplayReassert(cart) {
        if (!FIX_ROW_PRICES) return;

        for (var i = 0; i < DISPLAY_REASSERT_DELAYS.length; i++) {
            window.setTimeout(function () {
                neutraliseBoldPriceHooks();
                decorateRowPrices(cart);
            }, DISPLAY_REASSERT_DELAYS[i]);
        }
    }

    /**
     * sections/cart.liquid carries Bold theme hooks (bold_cart_total,
     * bold_cart_item_price, bold_cart_item_total) which Bold's remote options.js
     * can use to rewrite displayed prices. If it rewrites the subtotal after we
     * patch it, the shopper sees a number neither of us agrees on - so re-read
     * it shortly afterwards and record any drift for the next debug report.
     */
    function watchForDrift(expectedCents) {
        var totals = document.querySelectorAll('.total__price');
        if (!totals.length) return;

        var ours = textOf(totals[0]);

        window.setTimeout(function () {
            var now = textOf(document.querySelectorAll('.total__price')[0]);
            if (now === ours) return;

            var drift = {
                at: new Date().toISOString(),
                weWrote: ours,
                nowShows: now,
                cartTotal: expectedCents
            };
            writeStore(DRIFT_KEY, drift);
            console.warn(LOG, 'the displayed subtotal was overwritten after this script patched it. ' +
                'Something else on the page (most likely Bold options.js via the bold_cart_total hook) ' +
                'is rewriting prices.', drift);
        }, 2500);
    }

    /* -------------------------------------------------- apply */

    function apply(plan) {
        var chain = Promise.resolve();

        // Adds first: each merges into an existing identical line, so any
        // reduction of a line an add merged into is recomputed from the
        // reloaded cart rather than from a half-applied state.
        plan.adds.forEach(function (add) {
            chain = chain.then(function () {
                return postJson('/cart/add.js', add);
            });
        });

        return chain.then(function () {
            if (!countKeys(plan.updates)) return null;
            return postJson('/cart/update.js', { updates: plan.updates });
        });
    }

    /* -------------------------------------------------- entry */

    var lastReport = '';
    var prefetchedCart = null;

    // Every pass goes through this one chain, so passes never overlap.
    var passChain = Promise.resolve();
    var passesRun = 0;

    function fetchCart() {
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (response) {
            if (!response.ok) throw new Error('/cart.js -> HTTP ' + response.status);
            return response.json();
        });
    }

    // The first pass uses the copy requested while the page was still parsing,
    // which takes the round trip off the critical path entirely.
    function loadCart() {
        var pending = prefetchedCart;
        prefetchedCart = null;

        if (!pending) return fetchCart();
        return pending.then(function (cart) { return cart || fetchCart(); });
    }

    // Resolves once the report has been rendered, so callers can await it before
    // reloading the page out from under it.
    function finish(context) {
        if (!context.mode.debug) return Promise.resolve();

        return probeChargeProducts().then(function (probe) {
            context.probe = probe;
        }, function () {
            context.probe = null;
        }).then(function () {
            lastReport = buildReport(context);

            var history = readStore(HISTORY_KEY, []);
            history.push({ at: new Date().toISOString(), summary: context.summary || 'no summary' });
            while (history.length > MAX_HISTORY_PASSES) history.shift();
            writeStore(HISTORY_KEY, history);

            console.log(LOG + ' debug report (copy from the panel at the bottom of the page, or from here):\n\n' + lastReport);

            if (document.body) {
                showPanel(lastReport);
            } else {
                window.addEventListener('DOMContentLoaded', function () { showPanel(lastReport); });
            }
        });
    }

    function run(options) {
        var mode = resolveMode();
        var takeover = TAKE_OVER_LEGACY_PURGE && !mode.keepLegacy;
        var context = {
            mode: mode,
            dryRun: !!mode.dry,
            takeover: takeover,
            trigger: (options && options.trigger) || 'manual run()',
            legacyMatches: [],
            summary: ''
        };

        // Consume the breadcrumb from any reload we caused, so this page load
        // can be attributed and the crumb is not misread on a later pass.
        context.previousReload = readStore(RELOAD_KEY, null);
        dropStore(RELOAD_KEY);

        if (typeof fetch !== 'function' || typeof Promise !== 'function') {
            console.warn(LOG, 'fetch/Promise unavailable, skipping');
            return Promise.resolve();
        }

        // Fresh state, not the Liquid-rendered {{ cart.items | json }}: Bold can
        // write to the cart during this same page load.
        return loadCart().then(function (cart) {
            context.cart = cart;

            var items = (cart && cart.items) || [];
            var i;

            for (i = 0; i < items.length; i++) {
                var variantId = variantIdOf(items[i]);
                for (var e = 0; e < EXCLUDED_VARIANT_IDS.length; e++) {
                    if (EXCLUDED_VARIANT_IDS[e] === variantId) {
                        context.legacyMatches.push('variant ' + variantId + ' qty ' + items[i].quantity +
                            ' - "' + (items[i].product_title || items[i].title) + '" (handle: ' + items[i].handle + ')');
                    }
                }
            }

            // With the takeover off we cannot safely touch the excluded
            // variants: the legacy purge is about to delete them and hard-reload,
            // so anything written here would be undone or would start a fight.
            if (!takeover && context.legacyMatches.length) {
                context.stoodDown = true;
                context.summary = 'stood down for the legacy purge (' + context.legacyMatches.length + ' matching line item(s))';
                context.plan = buildPlan(cart, { excludedVariantIds: EXCLUDED_VARIANT_IDS });
                console.info(LOG, 'legacy personalization charge purge pending, skipping this load');
                releaseCheckout('stood down for the legacy purge');
                return finish(context);
            }

            // Do this before anything touches the prices, so Bold cannot undo
            // our writes from underneath.
            if (!context.dryRun) context.boldHooks = neutraliseBoldPriceHooks();

            var plan = buildPlan(cart, { excludedVariantIds: takeover ? [] : EXCLUDED_VARIANT_IDS });
            context.plan = plan;

            var changes = countKeys(plan.updates) + plan.adds.length;

            if (context.dryRun) {
                context.summary = 'dry run, ' + changes + ' change(s) would be applied';
                releaseCheckout('dry run - nothing to wait for');
                return finish(context);
            }

            if (!changes) {
                dropStore(GUARD_KEY);

                // The cart is right, but the rendered prices are not - Bold
                // rewrites them wrongly and the Liquid figures exclude the
                // charge. Both need correcting on every load, not only when a
                // fix happened. (Hooks were already neutralised above; a dry
                // run must not touch the page at all.)
                if (!context.dryRun) {
                    context.subtotal = repaintSubtotal(cart);
                    context.rowPrices = decorateRowPrices(cart);
                    scheduleDisplayReassert(cart);
                }

                context.summary = 'no changes needed' +
                    (context.rowPrices ? '; ' + context.rowPrices.reason : ' (dry run, page untouched)');

                releaseCheckout('cart already correct');
                return finish(context);
            }

            var signature = signatureOf(cart);
            var guard = readStore(GUARD_KEY, {});
            var count = guard.count || 0;

            if (count >= MAX_FIXES_PER_SESSION) {
                context.guardBlocked = 'already made ' + count + ' fix attempt(s) this session (cap ' + MAX_FIXES_PER_SESSION + ')';
                context.summary = 'blocked by loop guard (attempt cap)';
                console.warn(LOG, 'gave up after ' + count + ' attempt(s) this session; intended plan was',
                    { updates: plan.updates, adds: plan.adds });
                releaseCheckout('loop guard reached its attempt cap - not fixing further');
                return finish(context);
            }

            if (guard.signature === signature) {
                // We already remediated this exact cart state and it came back
                // unchanged - something is re-creating the charges. Stop.
                context.guardBlocked = 'this exact cart state was already fixed once this session and came back unchanged';
                context.summary = 'blocked by loop guard (no convergence)';
                console.warn(LOG, 'cart state did not converge after a fix, stopping; intended plan was',
                    { updates: plan.updates, adds: plan.adds });
                releaseCheckout('cart state did not converge - not fixing further');
                return finish(context);
            }

            writeStore(GUARD_KEY, { signature: signature, count: count + 1 });
            console.info(LOG, 'fixing personalization charges', { updates: plan.updates, adds: plan.adds });

            return apply(plan).then(function () {
                context.applied = changes;

                // Re-read the cart so the page is patched from what the server
                // actually holds, not from what we predicted it would hold.
                return fetchCart().then(function (after) {
                    context.cartAfter = after;

                    var outcome = patchPage(cart, after);
                    context.patched = outcome.patched;
                    context.patchReason = outcome.reason;
                    context.summary = 'applied ' + changes + ' change(s), ' +
                        (context.patched ? 'patched the page in place' : 'reloading: ' + outcome.reason);

                    if (context.patched) {
                        context.rowPrices = decorateRowPrices(after);
                        context.summary += '; ' + context.rowPrices.reason;
                        scheduleDisplayReassert(after);
                        watchForDrift(after.total_price);

                        // Cart is fixed and the page matches it - let checkout go.
                        releaseCheckout('cart reconciled and page patched');
                    } else {
                        // Leave a breadcrumb, so the next page load can say this
                        // reload was ours rather than something else's.
                        writeStore(RELOAD_KEY, { at: new Date().toISOString(), reason: outcome.reason });
                        // Keep the checkout gate up: this page is going away.
                        checkoutGate.reloadPending = true;
                    }

                    // A patched page is already consistent, so nothing is
                    // re-checking it - clear the guard rather than leaving a
                    // stale signature to block the next genuine fix.
                    if (context.patched) dropStore(GUARD_KEY);


                    return finish(context).then(function () {
                        if (context.patched) return;

                        // In debug mode give the panel and the console line a beat
                        // to land before the reload wipes them. The pass is in
                        // history either way, so the next report shows it too.
                        if (context.mode.debug) {
                            window.setTimeout(function () { window.location.reload(); }, 1500);
                            return;
                        }
                        window.location.reload();
                    });
                });
            }, function (err) {
                // Never reload on a failed mutation - the cart is half-written
                // and a reload would hide which step failed.
                context.applyError = String(err && err.message ? err.message : err);
                context.summary = 'apply failed: ' + context.applyError;
                console.warn(LOG, 'apply failed, cart left as-is:', err);
                releaseCheckout('apply failed - not holding checkout any longer');
                return finish(context);
            });
        }, function (err) {
            // Never leave the cart page broken because reconciliation failed.
            context.error = String(err && err.message ? err.message : err);
            context.summary = 'failed: ' + context.error;
            console.warn(LOG, 'reconciliation failed, cart left untouched:', err);
            releaseCheckout('reconciliation failed - not holding checkout any longer');
            return finish(context);
        });
    }

    window.GradwearPersonalizationChargeFix = {
        version: VERSION,
        run: run,
        buildPlan: buildPlan,
        // Resolves when every scheduled pass has finished - handy for
        // `await GradwearPersonalizationChargeFix.settled()` in the console.
        settled: function () { return passChain; },
        report: function () {
            if (!lastReport) {
                console.info(LOG, 'no report yet - load /cart?pcfix=debug or call run() in debug mode');
                return '';
            }
            console.log(lastReport);
            showPanel(lastReport);
            return lastReport;
        },
        reset: function () {
            dropStore(GUARD_KEY);
            dropStore(MODE_KEY);
            dropStore(HISTORY_KEY);
            console.info(LOG, 'debug mode and loop guard cleared');
        }
    };

    if (!isCartPage()) return;

    // Must happen NOW, at script-execution time: the legacy presenter's purge is
    // wired to DOMContentLoaded, which has not fired yet for a deferred script.
    // Waiting until window load would be far too late.
    if (TAKE_OVER_LEGACY_PURGE && !resolveMode().keepLegacy) suppressLegacyPurge();

    // Also now: the gate has to be listening before the shopper can click.
    installCheckoutGate();

    // Ask for the cart immediately, in parallel with the rest of page parsing,
    // so the first pass has it in hand the moment the DOM is ready.
    if (typeof fetch === 'function' && typeof Promise === 'function') {
        prefetchedCart = fetchCart().then(null, function () { return null; });
    }

    // Passes are serialised through one chain (declared above): a second pass
    // always sees the result of the first, so the two can never both add the
    // same charge.
    function schedulePass(trigger) {
        passChain = passChain.then(function () {
            if (passesRun >= MAX_PASSES_PER_PAGE) return;

            passesRun++;
            var isFirstPass = passesRun === 1;

            return Promise.resolve(run({ trigger: trigger })).then(function (result) {
                // The cart is trustworthy once the first pass is done; the
                // window-load pass is only a re-check for late Bold writes, and
                // holding checkout until then would be a needless wait.
                if (isFirstPass) releaseCheckout('first reconciliation pass finished');
                return result;
            }, function (err) {
                // Never strand the shopper because a pass failed.
                if (isFirstPass) releaseCheckout('first pass failed: ' + (err && err.message ? err.message : err));
                throw err;
            });
        }, function () { /* a failed pass must not block the next one */ });

        return passChain;
    }

    if (document.readyState === 'complete') {
        schedulePass('script-execution (page already loaded)');
    } else {
        if (RUN_AT_DOM_READY) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', function () { schedulePass('DOMContentLoaded'); });
            } else {
                schedulePass('script-execution (DOM already parsed)');
            }
        }

        // Second, cheap pass: catches charge line items Bold's options.js writes
        // after the DOM was ready. Finds nothing to do in the normal case.
        window.addEventListener('load', function () { schedulePass('window load (re-check)'); });
    }
}());